import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ShieldAlert, LogIn, ArrowLeft, ShieldCheck } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { post, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const LoginSchema = z.object({
  email: z.string().email('Valid email required'),
  password: z.string().min(8, 'Min 8 chars')
});
type LoginForm = z.infer<typeof LoginSchema>;

const TotpSchema = z.object({
  totp_code: z.string().regex(/^\d{6}$/, '6-digit code')
});
type TotpForm = z.infer<typeof TotpSchema>;

const RecoverySchema = z.object({
  recovery_code: z.string().trim().min(8, 'Enter your recovery code').max(40)
});
type RecoveryForm = z.infer<typeof RecoverySchema>;

interface LoginSuccessResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: {
    id: string;
    society_id: string | null;
    role: 'super_admin';
    email: string;
    full_name: string;
  };
}

interface LoginTotpChallengeResponse {
  requires_totp: true;
  login_challenge_id: string;
  expires_in: number;
}

type LoginResponse = LoginSuccessResponse | LoginTotpChallengeResponse;

function isTotpChallenge(res: LoginResponse): res is LoginTotpChallengeResponse {
  return (res as LoginTotpChallengeResponse).requires_totp === true;
}

interface ChallengeBody {
  login_challenge_id: string;
  totp_code?: string;
  recovery_code?: string;
}

type Step = 'credentials' | 'totp' | 'recovery';

export function SuperAdminLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('credentials');
  const [challengeId, setChallengeId] = useState<string | null>(null);

  const form = useForm<LoginForm>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' }
  });

  const totpForm = useForm<TotpForm>({
    resolver: zodResolver(TotpSchema),
    defaultValues: { totp_code: '' }
  });

  const recoveryForm = useForm<RecoveryForm>({
    resolver: zodResolver(RecoverySchema),
    defaultValues: { recovery_code: '' }
  });

  const totpInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus the 6-digit input when we advance to the TOTP step.
  useEffect(() => {
    if (step === 'totp') {
      // RHF owns the ref via register; nudge focus after the input renders.
      const t = window.setTimeout(() => totpInputRef.current?.focus(), 10);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [step]);

  const applySession = (res: LoginSuccessResponse): void => {
    setSession({
      accessToken: res.access_token,
      refreshToken: res.refresh_token,
      expiresIn: res.expires_in,
      user: { id: res.user.id, society_id: null, role: 'super_admin' }
    });
    navigate({ to: '/superadmin/dashboard' });
  };

  const credentialsLogin = useMutation({
    mutationFn: (values: LoginForm) => post<LoginResponse, LoginForm>('/superadmin/login', values),
    onSuccess: (res) => {
      if (isTotpChallenge(res)) {
        setChallengeId(res.login_challenge_id);
        setStep('totp');
        totpForm.reset({ totp_code: '' });
        recoveryForm.reset({ recovery_code: '' });
        return;
      }
      applySession(res);
    },
    onError: (e) => setErrMsg(getErrorMessage(e))
  });

  const challengeLogin = useMutation({
    mutationFn: (body: ChallengeBody) =>
      post<LoginResponse, ChallengeBody>('/superadmin/login', body),
    onSuccess: (res) => {
      if (isTotpChallenge(res)) {
        // Server re-issued a challenge — keep the same ID.
        setChallengeId(res.login_challenge_id);
        setErrMsg('Code rejected. Please try again.');
        return;
      }
      applySession(res);
    },
    onError: (e) => setErrMsg(getErrorMessage(e))
  });

  const onTotpSubmit = (v: TotpForm): void => {
    if (!challengeId) {
      setErrMsg('Login challenge expired. Please sign in again.');
      setStep('credentials');
      return;
    }
    setErrMsg(null);
    challengeLogin.mutate({ login_challenge_id: challengeId, totp_code: v.totp_code });
  };

  const onRecoverySubmit = (v: RecoveryForm): void => {
    if (!challengeId) {
      setErrMsg('Login challenge expired. Please sign in again.');
      setStep('credentials');
      return;
    }
    setErrMsg(null);
    challengeLogin.mutate({
      login_challenge_id: challengeId,
      recovery_code: v.recovery_code.trim().toUpperCase()
    });
  };

  const backToCredentials = (): void => {
    setStep('credentials');
    setChallengeId(null);
    setErrMsg(null);
    totpForm.reset({ totp_code: '' });
    recoveryForm.reset({ recovery_code: '' });
  };

  return (
    <div className="grid min-h-screen place-items-center bg-vt-bg px-vt-4">
      <Card className="w-full max-w-md">
        {step === 'credentials' ? (
          <>
            <CardHeader>
              <div className="mb-vt-2 flex h-12 w-12 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]">
                <ShieldAlert className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <CardTitle>Super-admin sign in</CardTitle>
              <CardDescription>
                Cross-tenant SaaS console. Email + password login (separate from per-society admin OTP).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={form.handleSubmit((v) => {
                  setErrMsg(null);
                  credentialsLogin.mutate(v);
                })}
                className="flex flex-col gap-vt-3"
              >
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    aria-invalid={Boolean(form.formState.errors.email) || undefined}
                    {...form.register('email')}
                  />
                  {form.formState.errors.email ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.email.message}</p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    aria-invalid={Boolean(form.formState.errors.password) || undefined}
                    {...form.register('password')}
                  />
                  {form.formState.errors.password ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.password.message}</p>
                  ) : null}
                </div>
                {errMsg ? (
                  <div className="rounded-vt-sm border border-vt-alert/30 bg-[color:var(--vt-alert-100)] px-vt-3 py-vt-2 text-vt-12 text-vt-alert">
                    {errMsg}
                  </div>
                ) : null}
                <Button type="submit" loading={credentialsLogin.isPending} loadingText="Signing in…" className="mt-vt-2">
                  <LogIn className="h-4 w-4" /> Sign in
                </Button>
              </form>
              <p className="mt-vt-4 text-center text-vt-12 text-vt-text-soft">
                Society admin?{' '}
                <a href="/login" className="text-vt-primary underline-offset-2 hover:underline">
                  Sign in here
                </a>
              </p>
            </CardContent>
          </>
        ) : null}

        {step === 'totp' ? (
          <>
            <CardHeader>
              <div className="mb-vt-2 flex h-12 w-12 items-center justify-center rounded-vt-md bg-[color:var(--vt-primary-100)] text-vt-primary">
                <ShieldCheck className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <CardTitle>Two-factor authentication</CardTitle>
              <CardDescription>
                Enter the 6-digit code from your authenticator app.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={totpForm.handleSubmit(onTotpSubmit)}
                className="flex flex-col gap-vt-3"
              >
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="totp-code">Authenticator code</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    aria-invalid={Boolean(totpForm.formState.errors.totp_code) || undefined}
                    className="text-center font-mono text-vt-24 tracking-[0.4em]"
                    {...totpForm.register('totp_code', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                        totpForm.setValue('totp_code', digits, { shouldValidate: digits.length === 6 });
                        if (digits.length === 6) {
                          void totpForm.handleSubmit(onTotpSubmit)();
                        }
                      }
                    })}
                    ref={(el) => {
                      totpInputRef.current = el;
                      totpForm.register('totp_code').ref(el);
                    }}
                  />
                  {totpForm.formState.errors.totp_code ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">{totpForm.formState.errors.totp_code.message}</p>
                  ) : null}
                </div>
                {errMsg ? (
                  <div className="rounded-vt-sm border border-vt-alert/30 bg-[color:var(--vt-alert-100)] px-vt-3 py-vt-2 text-vt-12 text-vt-alert">
                    {errMsg}
                  </div>
                ) : null}
                <Button type="submit" loading={challengeLogin.isPending} loadingText="Verifying…" className="mt-vt-2">
                  <LogIn className="h-4 w-4" /> Verify and sign in
                </Button>
                <div className="flex items-center justify-between pt-vt-2 text-vt-12">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-vt-text-soft hover:text-vt-text"
                    onClick={backToCredentials}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </button>
                  <button
                    type="button"
                    className="text-vt-primary underline-offset-2 hover:underline"
                    onClick={() => {
                      setErrMsg(null);
                      setStep('recovery');
                    }}
                  >
                    Lost your device? Use a recovery code
                  </button>
                </div>
              </form>
            </CardContent>
          </>
        ) : null}

        {step === 'recovery' ? (
          <>
            <CardHeader>
              <div className="mb-vt-2 flex h-12 w-12 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]">
                <ShieldCheck className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <CardTitle>Recovery code</CardTitle>
              <CardDescription>
                Each recovery code can only be used once. After signing in, regenerate your recovery codes from the operators page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={recoveryForm.handleSubmit(onRecoverySubmit)}
                className="flex flex-col gap-vt-3"
              >
                <div className="flex flex-col gap-vt-1">
                  <Label htmlFor="recovery-code">Recovery code</Label>
                  <Input
                    id="recovery-code"
                    type="text"
                    autoComplete="one-time-code"
                    aria-invalid={Boolean(recoveryForm.formState.errors.recovery_code) || undefined}
                    className="font-mono uppercase tracking-widest"
                    autoFocus
                    {...recoveryForm.register('recovery_code')}
                  />
                  {recoveryForm.formState.errors.recovery_code ? (
                    <p className="text-vt-12 text-vt-alert" role="alert">{recoveryForm.formState.errors.recovery_code.message}</p>
                  ) : null}
                </div>
                {errMsg ? (
                  <div className="rounded-vt-sm border border-vt-alert/30 bg-[color:var(--vt-alert-100)] px-vt-3 py-vt-2 text-vt-12 text-vt-alert">
                    {errMsg}
                  </div>
                ) : null}
                <Button type="submit" loading={challengeLogin.isPending} loadingText="Verifying…" className="mt-vt-2">
                  <LogIn className="h-4 w-4" /> Verify and sign in
                </Button>
                <div className="flex items-center justify-between pt-vt-2 text-vt-12">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-vt-text-soft hover:text-vt-text"
                    onClick={backToCredentials}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </button>
                  <button
                    type="button"
                    className="text-vt-primary underline-offset-2 hover:underline"
                    onClick={() => {
                      setErrMsg(null);
                      setStep('totp');
                    }}
                  >
                    Use authenticator code instead
                  </button>
                </div>
              </form>
            </CardContent>
          </>
        ) : null}
      </Card>
    </div>
  );
}
